import type { CityEntry, LocationSlot } from './types.js';
import {
  fuzzySearchCities, getCurrentTimeString, getCurrentDateString, getDSTWarning,
  getCurrentOffset, getAbbr, formatOffset, formatLocalTime, toLocal, getTransitions,
  hasIrregularDSTRule, auditRecurring,
} from './timezone.js';
import type { RankedHour, RotationOption, SlotHourEval } from './timezone.js';
import { buildInviteLine } from './ics.js';
import type { HolidayData } from './holidays.js';
import { findHolidays, dateKey } from './holidays.js';

// Location colors for visual distinction. Kept in step with the CSS
// [data-location-color] scale and with LOCATION_COLORS in timeline.ts.
export const LOCATION_COLORS = [
  '#6366f1', '#0B6B62', '#C97A16', '#B4437F', '#7C5CD6', '#1D7FA3',
  '#B4433A', '#5E7F1E', '#8B4FBF', '#146C8C', '#C25E1C', '#1F7A55',
];

export const MAX_LOCATIONS = 12;

/** How many near-miss options to offer when nothing works for everyone */
const NEAR_MISS_COUNT = 5;

export interface RenderContext {
  slots: LocationSlot[];
  /** Instant used for all offset math */
  at: Date;
  /** Calendar date of the meeting, as a UTC day */
  date: Date;
  anchorIndex: number;
  durationMinutes: number;
  isToday: boolean;
}

export interface ResultHandlers {
  onDownloadIcs(utcHour: number, recurring: boolean): void;
  onCopyLine(utcHour: number): void;
}

// === Location cards ===

export function renderLocationSlots(
  container: HTMLElement,
  ctx: RenderContext,
  onRemove: (index: number) => void,
  onWorkingHoursChange: (index: number, start: number, end: number) => void,
  onLabelChange: (index: number, label: string) => void
): void {
  container.innerHTML = '';

  for (let i = 0; i < ctx.slots.length; i++) {
    const slot = ctx.slots[i]!;
    const color = LOCATION_COLORS[i % LOCATION_COLORS.length]!;
    const offset = getCurrentOffset(slot.timezone, ctx.at);

    const card = document.createElement('div');
    card.className = 'location-card';
    card.style.setProperty('--location-color', color);
    if (i === ctx.anchorIndex) card.classList.add('is-anchor');

    const showRemove = ctx.slots.length > 1;
    const isStable = !slot.timezone.dst;
    const hasLabel = slot.label !== slot.city.name;

    // Primary reading is the local clock; the zone identity sits beside it in
    // one quiet meta line rather than three separate rows.
    const bigTime = ctx.isToday
      ? getCurrentTimeString(slot.timezone, ctx.at)
      : getCurrentDateString(slot.timezone, ctx.at);
    const sub = ctx.isToday
      ? `${getCurrentDateString(slot.timezone, ctx.at)} · ${getAbbr(slot.timezone, ctx.at)} ${formatOffset(offset)}`
      : `${getAbbr(slot.timezone, ctx.at)} ${formatOffset(offset)}`;

    card.innerHTML = `
      <div class="card-header">
        <div>
          <div class="name-row">
            <span class="city-name">${escapeHtml(slot.city.name)}</span>
            ${i === ctx.anchorIndex ? '<span class="badge badge-anchor" title="This location\'s local time stays fixed">Anchor</span>' : ''}
            ${isStable ? '<span class="badge badge-stable" title="No daylight saving — a reliable fixed reference">No DST</span>' : ''}
          </div>
          <div class="card-meta">${escapeHtml(slot.city.country)} · <span class="timezone-label">${escapeHtml(slot.timezone.id)}</span></div>
        </div>
        ${showRemove ? `<button class="remove-btn" data-index="${i}" title="Remove location" aria-label="Remove ${escapeHtml(slot.city.name)}">×</button>` : ''}
      </div>
      <div class="time-row">
        <span class="current-time">${escapeHtml(bigTime)}</span>
        <span class="card-sub">${escapeHtml(sub)}</span>
      </div>
      <input class="person-label" type="text" data-index="${i}" maxlength="40"
             placeholder="Add a name" value="${hasLabel ? escapeHtml(slot.label) : ''}"
             aria-label="Name for ${escapeHtml(slot.city.name)}">
      <div class="working-hours">
        <span class="wh-label">Works</span>
        <select name="work-start" data-index="${i}" aria-label="Working hours start">${hourOptions(slot.workingStart)}</select>
        <span>–</span>
        <select name="work-end" data-index="${i}" aria-label="Working hours end">${hourOptions(slot.workingEnd)}</select>
      </div>
    `;

    if (showRemove) {
      card.querySelector('.remove-btn')?.addEventListener('click', () => onRemove(i));
    }

    const startSelect = card.querySelector('select[name="work-start"]') as HTMLSelectElement | null;
    const endSelect = card.querySelector('select[name="work-end"]') as HTMLSelectElement | null;
    startSelect?.addEventListener('change', () => {
      onWorkingHoursChange(i, parseInt(startSelect.value, 10), parseInt(endSelect?.value ?? '17', 10));
    });
    endSelect?.addEventListener('change', () => {
      onWorkingHoursChange(i, parseInt(startSelect?.value ?? '9', 10), parseInt(endSelect.value, 10));
    });

    const labelInput = card.querySelector('.person-label') as HTMLInputElement | null;
    labelInput?.addEventListener('change', () => onLabelChange(i, labelInput.value.trim()));

    container.appendChild(card);
  }
}

// === Hero clocks ===

/**
 * The masthead carries a live read-out of UTC and every selected city, so the
 * page answers "what time is it there?" before the visitor touches a control.
 * No-ops when the markup is absent (content pages).
 */
export function renderNowRail(ctx: RenderContext): void {
  const rail = document.getElementById('now-rail');
  const utcEl = document.getElementById('now-utc');
  const listEl = document.getElementById('now-list');
  if (!rail || !utcEl || !listEl) return;

  if (!ctx.isToday || ctx.slots.length === 0) {
    rail.hidden = true;
    return;
  }
  rail.hidden = false;

  const now = ctx.at;
  utcEl.innerHTML = `${String(now.getUTCHours()).padStart(2, '0')}:${String(now.getUTCMinutes()).padStart(2, '0')} <span>UTC</span>`;

  listEl.innerHTML = ctx.slots.map((slot, i) => {
    const color = LOCATION_COLORS[i % LOCATION_COLORS.length]!;
    return `<li>
      <span class="dot" style="background:${color}"></span>
      <span class="city">${escapeHtml(slot.city.name)}</span>
      <span class="time">${escapeHtml(getCurrentTimeString(slot.timezone, now))}</span>
    </li>`;
  }).join('');
}

// === Autocomplete ===

export function setupAutocomplete(
  wrapper: HTMLElement,
  input: HTMLInputElement,
  dropdown: HTMLElement,
  cities: CityEntry[],
  onSelect: (city: CityEntry) => void
): void {
  let selectedIndex = -1;

  input.addEventListener('input', () => {
    const results = fuzzySearchCities(input.value, cities, 8);
    dropdown.innerHTML = '';
    selectedIndex = -1;

    if (results.length === 0) {
      dropdown.classList.remove('visible');
      return;
    }
    dropdown.classList.add('visible');

    for (let i = 0; i < results.length; i++) {
      const city = results[i]!;
      const item = document.createElement('div');
      item.className = 'autocomplete-item';
      item.innerHTML = `
        <span class="item-city">${escapeHtml(city.name)}</span>
        <span class="item-country">${escapeHtml(city.country)}</span>
        <span class="item-tz">${escapeHtml(city.timezone)}</span>
      `;
      item.addEventListener('click', () => {
        onSelect(city);
        dropdown.classList.remove('visible');
        input.value = '';
      });
      item.addEventListener('mouseenter', () => {
        selectedIndex = i;
        updateSelection(dropdown, selectedIndex);
      });
      dropdown.appendChild(item);
    }
  });

  input.addEventListener('keydown', (e) => {
    const items = dropdown.querySelectorAll('.autocomplete-item');
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
      updateSelection(dropdown, selectedIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, 0);
      updateSelection(dropdown, selectedIndex);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && items[selectedIndex]) (items[selectedIndex] as HTMLElement).click();
    } else if (e.key === 'Escape') {
      dropdown.classList.remove('visible');
    }
  });

  document.addEventListener('click', (e) => {
    if (!wrapper.contains(e.target as Node)) dropdown.classList.remove('visible');
  });
}

// === Results ===

function amountOutside(e: SlotHourEval): string {
  return e.hoursOutside >= 1
    ? `${e.hoursOutside % 1 === 0 ? e.hoursOutside : e.hoursOutside.toFixed(1)}h`
    : `${Math.round(e.hoursOutside * 60)}min`;
}

function statusLabel(e: SlotHourEval): string {
  if (e.status === 'working') return 'within working hours';
  return e.status === 'unsociable'
    ? `night-time — ${amountOutside(e)} outside working hours`
    : `${amountOutside(e)} outside working hours`;
}

/**
 * A sentence, not a score. "1 stretched" told the visitor nothing actionable;
 * naming who absorbs the cost is the whole point of the ranking.
 */
function costLabel(r: RankedHour, slots: LocationSlot[]): string {
  if (r.fullOverlap) return 'Everyone inside working hours';
  const parts = r.strainedIndices.map(i => {
    const e = r.perSlot[i]!;
    const name = slots[i]!.label;
    return e.status === 'unsociable'
      ? `${name} at night`
      : `${name} stretches ${amountOutside(e)}`;
  });
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function qualityBadge(r: RankedHour): string {
  if (r.fullOverlap) return '<span class="badge badge-good">works for everyone</span>';
  const nights = r.perSlot.filter(p => p.status === 'unsociable').length;
  if (nights > 0) return `<span class="badge badge-bad">${nights} at night</span>`;
  return `<span class="badge badge-warn">${r.strainedIndices.length} stretched</span>`;
}

function timeCell(e: SlotHourEval): string {
  return `<td class="time-cell t-${e.status}" title="${escapeHtml(statusLabel(e))}"><span class="t">${escapeHtml(formatLocalTime(e.local))}</span></td>`;
}

function utcCell(utcHour: number): string {
  return `<td class="utc-cell">${String(utcHour).padStart(2, '0')}:00</td>`;
}

function rowActions(utcHour: number): string {
  return `<td class="row-actions">
    <button class="mini-btn" data-act="copy" data-hour="${utcHour}" title="Copy this time for every location">Copy</button>
    <button class="mini-btn" data-act="ics" data-hour="${utcHour}" title="Download calendar invite (.ics)">.ics</button>
  </td>`;
}

function tableHead(slots: LocationSlot[], last = 'Cost'): string {
  let html = '<thead><tr><th>UTC</th>';
  for (const s of slots) {
    html += `<th>${escapeHtml(s.label === s.city.name ? s.city.name : s.label)}</th>`;
  }
  return html + `<th>${escapeHtml(last)}</th><th>Use</th></tr></thead>`;
}

function legendAndActions(): string {
  return `<div class="table-foot">
      <div class="legend">
        <span><i class="sw t-working"></i> inside working hours</span>
        <span><i class="sw t-fringe"></i> stretched</span>
        <span><i class="sw t-unsociable"></i> night-time, 10 PM–7 AM</span>
      </div>
      <span class="spacer"></span>
      <div class="result-actions">
        <button class="btn-secondary" id="copy-times-btn">Copy all times</button>
        <button class="btn-secondary" id="copy-invite-btn">Copy invite line</button>
      </div>
    </div>`;
}

export function renderResults(
  container: HTMLElement,
  ctx: RenderContext,
  ranked: RankedHour[],
  rotation: RotationOption[],
  handlers: ResultHandlers
): void {
  const { slots } = ctx;

  if (slots.length === 0) {
    container.innerHTML = '<p class="muted">Add a location to get started.</p>';
    return;
  }

  const fullOverlaps = ranked.filter(r => r.fullOverlap);
  let html = '';
  let rows: RankedHour[];

  if (fullOverlaps.length > 0) {
    html += `<p class="results-lede">${fullOverlaps.length} hour${fullOverlaps.length === 1 ? '' : 's'} where everyone is inside their working day, fairest first.</p>`;
    rows = fullOverlaps;
  } else {
    // No hour works for everyone. This is the normal case for wide spreads, and
    // it is exactly when the user most needs a recommendation rather than a
    // shrug — so rank the least-bad options and name who absorbs the cost.
    rows = ranked.slice(0, NEAR_MISS_COUNT);
    html += `
      <div class="no-overlap">
        <span class="banner-mark" aria-hidden="true"></span>
        <div>
          <p>No hour works for everyone.</p>
          <p>With this spread there is no time when all ${slots.length} locations are inside their working day — that is a
          property of the geography, not a mistake. The closest options are ranked below by how much they ask of people;
          night hours (10 PM–7 AM local) count heavily, so an option that costs one person their sleep ranks below one
          that asks several people to stretch a little.</p>
        </div>
      </div>`;
  }

  html += `<div class="table-frame"><table class="meeting-times-table">${tableHead(slots)}<tbody>`;
  for (const r of rows) {
    html += '<tr>' + utcCell(r.utcHour) + r.perSlot.map(timeCell).join('') +
      `<td class="cost-cell">${escapeHtml(costLabel(r, slots))} ${qualityBadge(r)}</td>` +
      rowActions(r.utcHour) + '</tr>';
  }
  html += '</tbody></table>' + legendAndActions() + '</div>';

  if (fullOverlaps.length === 0) {
    const best = rows[0];
    if (best) {
      const strained = best.strainedIndices
        .map(i => `<strong>${escapeHtml(slots[i]!.label)}</strong> (${escapeHtml(statusLabel(best.perSlot[i]!))})`)
        .join(', ');
      html += `<p class="who-pays">At the top option the cost falls on ${strained}. Rotate it — see below.</p>`;
    }
  }

  // Rotation — the fair-scheduling feature, given its own panel.
  if (rotation.length > 1) {
    const topStrain = rows[0]?.strainedIndices
      .map(i => escapeHtml(slots[i]!.label)).join(', ');
    html += `
      <section class="rotation">
        <div class="rotation-grid">
          <div>
            <span class="eyebrow">The fair-scheduling feature</span>
            <h4>Rotate the burden</h4>
            <p>The same person should not always take the awkward slot. These are the kindest hours for each city in
            turn — alternate week to week and the cost lands on everybody equally.</p>
            ${topStrain ? `<p class="muted">At the top-ranked option, the cost falls on <strong>${topStrain}</strong>.</p>` : ''}
          </div>
          <div class="rotation-list">`;
    for (const opt of rotation) {
      const who = opt.ranked.perSlot.map((e, i) =>
        `<span class="who t-${e.status}">${escapeHtml(shortLabel(slots[i]!))} <b>${escapeHtml(formatLocalTime(e.local))}</b></span>`
      ).join('');
      html += `
            <div class="rotation-option">
              <div class="top">
                <span class="favours">Kindest to ${escapeHtml(slots[opt.favours]!.label)}</span>
                <button class="use" data-act="ics" data-hour="${opt.utcHour}">Use this</button>
              </div>
              <div class="times">
                <span class="utc">${String(opt.utcHour).padStart(2, '0')}:00 <span>UTC</span></span>
                <span class="spacer"></span>
                ${who}
              </div>
            </div>`;
    }
    html += `
            <p class="rotation-note">Add them as alternating weeks — each .ics keeps its own anchor intact.</p>
          </div>
        </div>
      </section>`;
  }

  container.innerHTML = html;

  container.querySelectorAll<HTMLButtonElement>('[data-act]').forEach(btn => {
    btn.addEventListener('click', () => {
      const hour = parseInt(btn.dataset['hour'] ?? '0', 10);
      if (btn.dataset['act'] === 'ics') handlers.onDownloadIcs(hour, false);
      else handlers.onCopyLine(hour);
    });
  });
}

/** "San Francisco" → "SF" for the dense rotation rows; short names pass through. */
function shortLabel(slot: LocationSlot): string {
  const label = slot.label === slot.city.name ? slot.city.name : slot.label;
  if (label.length <= 9) return label;
  const words = label.split(/\s+/);
  if (words.length > 1) return words.map(w => w[0]!.toUpperCase()).join('');
  return label.slice(0, 3).toUpperCase();
}

// === DST banner ===

/**
 * Warn about DST transitions in the next 60 days, and about the windows where
 * two locations' offsets move relative to one another — the failure that
 * silently shifts standing meetings for part of the team.
 */
export function renderDSTBanner(container: HTMLElement, ctx: RenderContext): void {
  const { slots, at } = ctx;
  const horizon = new Date(at.getTime() + 60 * 24 * 60 * 60 * 1000);
  const parts: string[] = [];

  for (const slot of slots) {
    const warning = getDSTWarning(slot.timezone, at, 60);
    if (warning) {
      parts.push(`<div class="dst-warning"><span class="banner-mark" aria-hidden="true"></span><span><strong>${escapeHtml(slot.city.name)}:</strong> ${escapeHtml(warning)}</span></div>`);
    }
  }

  // Relative shifts: for each pair, find dates where the gap between them changes.
  const shifts: string[] = [];
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i]!, b = slots[j]!;
      const dates = [...getTransitions(a.timezone, at, horizon), ...getTransitions(b.timezone, at, horizon)]
        .sort((x, y) => x.date.getTime() - y.date.getTime());
      for (const t of dates) {
        const before = new Date(t.date.getTime() - 36e5);
        const after = new Date(t.date.getTime() + 36e5);
        const gapBefore = getCurrentOffset(b.timezone, before) - getCurrentOffset(a.timezone, before);
        const gapAfter = getCurrentOffset(b.timezone, after) - getCurrentOffset(a.timezone, after);
        if (gapBefore !== gapAfter) {
          shifts.push(
            `<li><strong>${formatShortDate(t.date)}</strong>: ${escapeHtml(a.city.name)} → ${escapeHtml(b.city.name)} gap changes from ` +
            `${formatGap(gapBefore)} to ${formatGap(gapAfter)}</li>`
          );
        }
      }
    }
  }

  if (parts.length === 0 && shifts.length === 0) {
    container.innerHTML = '';
    return;
  }

  let html = parts.join('');
  if (shifts.length > 0) {
    const unique = [...new Set(shifts)];
    html += `<div class="dst-shifts">
      <strong>Offsets change during the next 60 days</strong>
      <p class="muted">Countries do not change clocks on the same date. Any recurring meeting across these pairs will
      move for one side during these windows.</p>
      <ul>${unique.join('')}</ul>
    </div>`;
  }
  container.innerHTML = html;
}

function formatGap(hours: number): string {
  const abs = Math.abs(hours);
  const whole = Math.floor(abs);
  const mins = Math.round((abs - whole) * 60);
  return mins === 0 ? `${whole}h` : `${whole}h${mins}m`;
}

function formatShortDate(d: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

// === Holidays ===

export function renderHolidayWarnings(
  container: HTMLElement,
  ctx: RenderContext,
  data: HolidayData | null,
  utcHour: number
): void {
  if (!data) {
    container.innerHTML = '';
    return;
  }

  const { slots, date, at } = ctx;

  // Each participant's local calendar date at the proposed hour.
  const localDates = slots.map(slot => {
    const offset = getCurrentOffset(slot.timezone, at);
    const local = toLocal(utcHour, offset);
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    d.setUTCDate(d.getUTCDate() + local.dayOffset);
    return dateKey(d);
  });

  const hits = findHolidays(data, slots.map(s => s.city.country), localDates);
  const found = hits.map((h, i) => ({ h, i })).filter(x => x.h);

  const unknown = slots
    .map((s, i) => ({ s, i }))
    .filter(x => {
      const c = data.countries.get(x.s.city.country);
      return !c || c.partial;
    });

  const outOfRange = !data.years.includes(date.getUTCFullYear());

  let html = '';
  for (const { h, i } of found) {
    html += `<div class="holiday-warning"><span class="banner-mark" aria-hidden="true"></span><span><strong>${escapeHtml(slots[i]!.label)}</strong> —
      ${escapeHtml(h!.holidayName)} is a public holiday in ${escapeHtml(h!.countryName)} on ${escapeHtml(h!.date)}.</span></div>`;
  }

  const caveats: string[] = [];
  if (outOfRange) {
    caveats.push(`Holiday data covers ${data.years.join(' and ')} only — no check was made for ${date.getUTCFullYear()}.`);
  }
  if (unknown.length > 0) {
    const names = [...new Set(unknown.map(u => u.s.city.country))].map(escapeHtml).join(', ');
    caveats.push(`Coverage for ${names} is partial or unavailable (holidays set by lunar or other non-Gregorian calendars are not listed) — check locally.`);
  }

  if (caveats.length > 0) {
    html += `<p class="holiday-caveat">${caveats.map(c => escapeHtml(c)).join(' ')} Source: ${escapeHtml(data.source)}.</p>`;
  }

  container.innerHTML = html;
}

// === Recurring audit ===

export function renderRecurringAudit(
  container: HTMLElement,
  ctx: RenderContext,
  anchorLocalHour: number
): void {
  const { slots, anchorIndex, date } = ctx;
  const anchor = slots[anchorIndex];
  if (!anchor || slots.length < 2) {
    container.innerHTML = '';
    return;
  }

  const rows = auditRecurring(slots, anchorIndex, anchorLocalHour, date, 12);
  const anchorTime = formatLocalTime({ minutes: anchorLocalHour * 60, dayOffset: 0 }, false);

  if (rows.length <= 1) {
    container.innerHTML = `
      <div class="audit-head">
        <h3>Recurring meeting check</h3>
        <p>Anchored to <strong>${escapeHtml(anchor.label)} at ${escapeHtml(anchorTime)}</strong>, nobody's local time
        changes over the next 12 months. Nothing to watch for.</p>
      </div>`;
    return;
  }

  const irregular = slots.filter(s => hasIrregularDSTRule(s.timezone)).map(s => s.city.name);

  let html = `
    <div class="audit-head">
      <h3>Recurring meeting check</h3>
      <p>Repeating weekly, anchored to <strong>${escapeHtml(anchor.label)} at ${escapeHtml(anchorTime)}</strong>.
      Everyone else's local time shifts on these dates over the next twelve months — send a heads-up before each one.</p>
    </div>
    <div class="table-scroll"><table class="meeting-times-table audit-table"><thead><tr><th>From</th>`;
  for (const s of slots) html += `<th>${escapeHtml(s.label)}</th>`;
  html += '<th>Cause</th></tr></thead><tbody>';

  for (const row of rows) {
    html += `<tr><td class="utc-cell">${escapeHtml(formatShortDate(row.date))} ${row.date.getUTCFullYear()}</td>`;
    for (let i = 0; i < row.localTimes.length; i++) {
      const changed = row.changedIndices.includes(i);
      html += `<td class="${changed ? 'cell-changed' : ''}">${escapeHtml(formatLocalTime(row.localTimes[i]!))}</td>`;
    }
    // Naming the cause turns a grid of numbers into an explanation.
    const cause = row.changedIndices.length === 0
      ? 'Current offsets'
      : `${row.changedIndices.map(i => escapeHtml(slots[i]!.label)).join(', ')} shifts`;
    html += `<td class="cause-cell">${cause}</td></tr>`;
  }
  html += '</tbody></table></div>';

  html += `<p class="audit-note">Projections past ${new Date().getUTCFullYear()} replay each zone's current transition rule.`;
  if (irregular.length > 0) {
    html += ` ${escapeHtml(irregular.join(', '))} set transition dates on a non-Gregorian calendar, so those rows are approximate.`;
  }
  html += '</p>';

  container.innerHTML = html;
}

// === Misc ===

export function showToast(message: string): void {
  document.querySelector('.copied-toast')?.remove();
  const toast = document.createElement('div');
  toast.className = 'copied-toast';
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2500);
}

export function buildInviteText(ctx: RenderContext, utcHour: number): string {
  return buildInviteLine(ctx.slots, utcHour, ctx.date);
}

function hourOptions(selected: number): string {
  let html = '';
  for (let h = 0; h < 24; h++) {
    const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
    html += `<option value="${h}"${h === selected ? ' selected' : ''}>${label}</option>`;
  }
  return html;
}

function updateSelection(dropdown: HTMLElement, index: number): void {
  dropdown.querySelectorAll('.autocomplete-item').forEach((item, i) => {
    if (i === index) {
      item.classList.add('selected');
      item.scrollIntoView({ block: 'nearest' });
    } else {
      item.classList.remove('selected');
    }
  });
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
